import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Car, 
  Truck, 
  Bus, 
  Bike,
  ChevronDown,
  ChevronUp,
  Check,
  AlertTriangle,
  Siren,
  Settings2
} from 'lucide-react';

interface VehicleType {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  color: string;
}

interface VehicleConfigPanelProps {
  selectedVehicles: string[];
  onVehicleChange: (vehicles: string[]) => void;
  confidenceThreshold: number;
  onConfidenceChange: (threshold: number) => void;
}

// All available vehicle types
const VEHICLE_TYPES: VehicleType[] = [
  { id: 'car', name: 'Coche', icon: <Car className="w-4 h-4" />, description: 'Automóviles y sedanes', color: 'bg-blue-500' },
  { id: 'truck', name: 'Camión', icon: <Truck className="w-4 h-4" />, description: 'Camiones de carga', color: 'bg-orange-500' },
  { id: 'bus', name: 'Autobús', icon: <Bus className="w-4 h-4" />, description: 'Buses y autocares', color: 'bg-green-500' },
  { id: 'motorcycle', name: 'Moto', icon: <Bike className="w-4 h-4" />, description: 'Motocicletas', color: 'bg-red-500' },
  { id: 'bicycle', name: 'Bicicleta', icon: <Bike className="w-4 h-4" />, description: 'Bicicletas', color: 'bg-cyan-500' },
  { id: 'van', name: 'Furgoneta', icon: <Truck className="w-4 h-4" />, description: 'Furgonetas y vans', color: 'bg-purple-500' },
  { id: 'pickup', name: 'Pickup', icon: <Truck className="w-4 h-4" />, description: 'Camionetas pickup', color: 'bg-amber-500' },
  { id: 'suv', name: 'SUV', icon: <Car className="w-4 h-4" />, description: 'Todoterrenos y SUVs', color: 'bg-emerald-500' },
  { id: 'trailer', name: 'Remolque', icon: <Truck className="w-4 h-4" />, description: 'Camiones con remolque', color: 'bg-slate-500' },
  { id: 'emergency', name: 'Emergencia', icon: <Siren className="w-4 h-4" />, description: 'Vehículos de emergencia', color: 'bg-rose-500' },
];

export function VehicleConfigPanel({
  selectedVehicles,
  onVehicleChange,
  confidenceThreshold,
  onConfidenceChange
}: VehicleConfigPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleVehicle = (vehicleId: string) => {
    if (selectedVehicles.includes(vehicleId)) {
      onVehicleChange(selectedVehicles.filter(v => v !== vehicleId));
    } else {
      onVehicleChange([...selectedVehicles, vehicleId]);
    }
  };

  const selectAll = () => {
    onVehicleChange(VEHICLE_TYPES.map(v => v.id));
  };

  const selectNone = () => {
    onVehicleChange([]);
  };

  const selectCommon = () => {
    onVehicleChange(['car', 'truck', 'bus', 'motorcycle', 'van']);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-xl overflow-hidden"
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-dark-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
            <Settings2 className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-dark-100">Configuración de Detección</h3>
            <p className="text-sm text-dark-400">
              {selectedVehicles.length} tipos de vehículo • {Math.round(confidenceThreshold * 100)}% confianza
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-dark-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-dark-400" />
        )}
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 border-t border-dark-800/50 space-y-4">
              
              {/* Confidence Threshold */}
              <div className="bg-dark-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-dark-200">Umbral de Confianza</span>
                  <span className="text-lg font-mono font-bold text-primary-400">
                    {Math.round(confidenceThreshold * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0.1"
                  max="0.95"
                  step="0.05"
                  value={confidenceThreshold}
                  onChange={(e) => onConfidenceChange(parseFloat(e.target.value))}
                  className="w-full h-2 bg-dark-700 rounded-lg appearance-none cursor-pointer
                           [&::-webkit-slider-thumb]:appearance-none
                           [&::-webkit-slider-thumb]:w-5
                           [&::-webkit-slider-thumb]:h-5
                           [&::-webkit-slider-thumb]:bg-primary-500
                           [&::-webkit-slider-thumb]:rounded-full
                           [&::-webkit-slider-thumb]:cursor-pointer"
                />
                <div className="flex justify-between mt-2">
                  <span className="text-xs text-dark-500">Más detecciones</span>
                  <span className="text-xs text-dark-500">Mayor precisión</span>
                </div>
              </div>

              {/* Quick selection buttons */}
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="flex-1 py-2 text-xs bg-dark-800 hover:bg-dark-700 text-dark-300 rounded-lg transition-colors"
                >
                  Todos
                </button>
                <button
                  onClick={selectCommon}
                  className="flex-1 py-2 text-xs bg-primary-500/20 hover:bg-primary-500/30 text-primary-400 rounded-lg transition-colors"
                >
                  Comunes
                </button>
                <button
                  onClick={selectNone}
                  className="flex-1 py-2 text-xs bg-dark-800 hover:bg-dark-700 text-dark-300 rounded-lg transition-colors"
                >
                  Ninguno
                </button>
              </div>

              {/* Vehicle type grid */}
              <div className="grid grid-cols-2 gap-2">
                {VEHICLE_TYPES.map(vehicle => {
                  const isSelected = selectedVehicles.includes(vehicle.id);
                  return (
                    <button
                      key={vehicle.id}
                      onClick={() => toggleVehicle(vehicle.id)}
                      className={`p-3 rounded-lg text-left transition-all flex items-center gap-3 ${
                        isSelected
                          ? 'bg-primary-500/20 border-2 border-primary-500'
                          : 'bg-dark-800/50 border-2 border-transparent hover:bg-dark-700/50'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg ${vehicle.color} flex items-center justify-center`}>
                        {vehicle.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${
                            isSelected ? 'text-primary-300' : 'text-dark-200'
                          }`}>
                            {vehicle.name}
                          </span>
                          {isSelected && (
                            <Check className="w-4 h-4 text-primary-400" />
                          )}
                        </div>
                        <p className="text-xs text-dark-500 truncate">{vehicle.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Warning if no vehicles selected */}
              {selectedVehicles.length === 0 && (
                <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                  <span className="text-sm text-amber-400">
                    Selecciona al menos un tipo de vehículo para detectar
                  </span>
                </div>
              )}

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default VehicleConfigPanel;
